import sanitizeHtml from 'sanitize-html';
import type { EmailThread as EmailThreadType } from '@Types/emailThread';
import {
  mergeThreadMessages,
  getThreadParticipantName,
  formatEmailTime,
  type EmailThreadMessage,
} from '@Utils/emailThread';

type EmailThreadProps = {
  thread?: EmailThreadType | null;
  className?: string;
};

const EmailThread = ({ thread, className = '' }: EmailThreadProps) => {
  const messages = mergeThreadMessages(thread);
  const participantName = getThreadParticipantName(thread);

  // Thread lists are fetched without their message collections, so an absent
  // collection means "not loaded yet" while an empty one means "no messages".
  const isLoaded =
    !!thread &&
    (thread.inboundMessages !== undefined ||
      thread.emailDeliveries !== undefined);

  if (!isLoaded) {
    return (
      <div className="p-4 text-center text-sm text-gray-400">
        Loading conversation…
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-gray-400">
        No messages in this thread yet.
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {messages.map((message) => (
        <MessageBubble
          key={`${message.direction}-${message.id}`}
          message={message}
          thread={thread}
          participantName={participantName}
        />
      ))}
    </div>
  );
};

function MessageBubble({
  message,
  thread,
  participantName,
}: {
  message: EmailThreadMessage;
  thread?: EmailThreadType | null;
  participantName: string;
}) {
  const isOutbound = message.direction === 'outbound';
  const html = message.outboundMessage?.html;
  const body = isOutbound
    ? message.outboundMessage?.messageBody
    : message.messageBody;

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
          isOutbound ? 'bg-[#CAAB6A] text-black' : 'bg-gray-100 text-gray-800'
        }`}
      >
        <div
          className={`mb-0.5 flex items-center gap-1.5 text-xs ${
            isOutbound ? 'text-black' : 'text-gray-500'
          }`}
        >
          <span>
            {isOutbound
              ? `${thread?.inbox?.user?.name ?? 'Concierge'} / ${thread?.inbox?.email ?? ''}`
              : participantName}
          </span>
          <span>·</span>
          <span>{formatEmailTime(message.createdAt)}</span>
        </div>

        {isOutbound && html ? (
          <div
            className="[&_a]:underline"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
          />
        ) : (
          <div className="whitespace-pre-wrap wrap-break-word">{body}</div>
        )}

        {isOutbound && (
          <div className="mt-1 text-right text-xs text-black">
            {message.openedAt ? 'Opened' : 'Sent'}
          </div>
        )}
      </div>
    </div>
  );
}

export default EmailThread;
